<script setup lang="ts">
import {
	ABOUT_MODAL_KEY,
	CHANGE_PASSWORD_MODAL_KEY,
	CHAT_EMBED_MODAL_KEY,
	DUPLICATE_MODAL_KEY,
	EXTERNAL_SECRETS_PROVIDER_MODAL_KEY,
	SECRETS_PROVIDER_CONNECTION_MODAL_KEY,
	DELETE_SECRETS_PROVIDER_MODAL_KEY,
	FROM_AI_PARAMETERS_MODAL_KEY,
	IMPORT_CURL_MODAL_KEY,
	IMPORT_WORKFLOW_URL_MODAL_KEY,
	LOG_STREAM_MODAL_KEY,
	MFA_SETUP_MODAL_KEY,
	VERSIONS_MODAL_KEY,
	NEW_ASSISTANT_SESSION_MODAL,
	NPS_SURVEY_MODAL_KEY,
	PROMPT_MFA_CODE_MODAL_KEY,
	SETUP_CREDENTIALS_MODAL_KEY,
	WHATS_NEW_MODAL_KEY,
	WORKFLOW_ACTIVATION_CONFLICTING_WEBHOOK_MODAL_KEY,
	WORKFLOW_ACTIVE_MODAL_KEY,
	WORKFLOW_DIFF_MODAL_KEY,
	WORKFLOW_EXTRACTION_NAME_MODAL_KEY,
	WORKFLOW_HISTORY_VERSION_RESTORE,
	WORKFLOW_HISTORY_VERSION_UNPUBLISH,
	WORKFLOW_HISTORY_NAME_VERSION_MODAL_KEY,
	WORKFLOW_SETTINGS_MODAL_KEY,
	WORKFLOW_SHARE_MODAL_KEY,
	EXPERIMENT_TEMPLATE_RECO_V2_KEY,
	EXPERIMENT_TEMPLATE_RECO_V3_KEY,
	CONFIRM_PASSWORD_MODAL_KEY,
	BINARY_DATA_VIEW_MODAL_KEY,
	STOP_MANY_EXECUTIONS_MODAL_KEY,
	ADD_EXECUTION_TO_DATASET_MODAL_KEY,
	MIGRATE_WORKFLOW_MODAL_KEY,
	WORKFLOW_DESCRIPTION_MODAL_KEY,
	WORKFLOW_PUBLISH_MODAL_KEY,
	WORKFLOW_HISTORY_PUBLISH_MODAL_KEY,
	CREDENTIAL_RESOLVER_EDIT_MODAL_KEY,
	AI_BUILDER_DIFF_MODAL_KEY,
	INSTANCE_AI_CREDENTIAL_SETUP_MODAL_KEY,
	INSTANCE_AI_TOOLS_CONNECTION_MODAL_KEY,
	AI_GATEWAY_TOP_UP_MODAL_KEY,
	AGENT_CONFIRMATION_MODAL_KEY,
	TRIAL_INTRO_MODAL_KEY,
} from '@/app/constants';
import {
	ANNOTATION_TAGS_MANAGER_MODAL_KEY,
	TAGS_MANAGER_MODAL_KEY,
} from '@/features/shared/tags/tags.constants';
import { DEBUG_PAYWALL_MODAL_KEY } from '@/features/execution/executions/executions.constants';
import { VARIABLE_MODAL_KEY } from '@/features/settings/environments.ee/environments.constants';
import {
	CREDENTIAL_EDIT_MODAL_KEY,
	CREDENTIAL_SELECT_MODAL_KEY,
} from '@/features/credentials/credentials.constants';
import {
	DELETE_USER_MODAL_KEY,
	INVITE_USER_MODAL_KEY,
	PERSONALIZATION_MODAL_KEY,
} from '@/features/settings/users/users.constants';
import { COMMUNITY_PLUS_ENROLLMENT_MODAL } from '@/features/settings/usage/usage.constants';
import {
	DELETE_FOLDER_MODAL_KEY,
	MOVE_FOLDER_MODAL_KEY,
} from '@/features/core/folders/folders.constants';
import {
	SOURCE_CONTROL_PULL_MODAL_KEY,
	SOURCE_CONTROL_PULL_RESULT_MODAL_KEY,
	SOURCE_CONTROL_PUSH_MODAL_KEY,
} from '@/features/integrations/sourceControl.ee/sourceControl.constants';
import { PROJECT_MOVE_RESOURCE_MODAL } from '@/features/collaboration/projects/projects.constants';
import {
	COMMUNITY_PACKAGE_CONFIRM_MODAL_KEY,
	COMMUNITY_PACKAGE_INSTALL_MODAL_KEY,
} from '@/features/settings/communityNodes/communityNodes.constants';
import { API_KEY_CREATE_OR_EDIT_MODAL_KEY } from '@/features/settings/apiKeys/apiKeys.constants';
import type { ApiKeyWithRawValue } from '@n8n/api-types';
import AboutModal from '@/app/components/AboutModal.vue';
import ActivationModal from '@/app/components/ActivationModal.vue';
import ApiKeyCreateOrEditModal from '@/features/settings/apiKeys/components/ApiKeyCreateOrEditModal.vue';
import NewAssistantSessionModal from '@/features/ai/assistant/components/Chat/NewAssistantSessionModal.vue';
import ChangePasswordModal from '@/features/core/auth/components/ChangePasswordModal.vue';
import ConfirmPasswordModal from '@/features/core/auth/components/ConfirmPasswordModal.vue';
import ChatEmbedModal from '@/app/components/ChatEmbedModal.vue';
import CommunityPackageInstallModal from '@/features/settings/communityNodes/components/CommunityPackageInstallModal.vue';
import CommunityPackageManageConfirmModal from '@/features/settings/communityNodes/components/CommunityPackageManageConfirmModal.vue';
import CommunityPlusEnrollmentModal from '@/features/settings/usage/components/CommunityPlusEnrollmentModal.vue';
import CredentialEdit from '@/features/credentials/components/CredentialEdit/CredentialEdit.vue';
import CredentialsSelectModal from '@/features/credentials/components/CredentialsSelectModal.vue';
import DebugPaywallModal from '@/features/execution/executions/components/DebugPaywallModal.vue';
import DeleteFolderModal from '@/features/core/folders/components/DeleteFolderModal.vue';
import MoveToFolderModal from '@/features/core/folders/components/MoveToFolderModal.vue';
import DeleteUserModal from '@/features/settings/users/components/DeleteUserModal.vue';
import DuplicateWorkflowDialog from '@/app/components/DuplicateWorkflowDialog.vue';
import ExternalSecretsProviderModal from '@/features/integrations/externalSecrets.ee/components/ExternalSecretsProviderModal.ee.vue';
import SecretsProviderConnectionModal from '@/features/integrations/secretsProviders.ee/components/SecretsProviderConnectionModal.ee.vue';
import DeleteSecretsProviderModal from '@/features/integrations/secretsProviders.ee/components/DeleteSecretsProviderModal.ee.vue';
import FromAiParametersModal from '@/app/components/FromAiParametersModal.vue';
import ImportCurlModal from '@/features/ndv/parameters/components/ImportCurlModal.vue';
import BinaryDataViewModal from '@/features/ndv/runData/components/BinaryDataViewModal.vue';
import ImportWorkflowUrlModal from '@/app/components/ImportWorkflowUrlModal.vue';
import InviteUsersModal from '@/features/settings/users/components/InviteUsersModal.vue';
import MfaSetupModal from '@/features/core/auth/components/MfaSetupModal.vue';
import ModalRoot from '@/app/components/ModalRoot.vue';
import NpsSurvey from '@/app/components/NpsSurvey.vue';
import PersonalizationModal from '@/features/settings/users/components/PersonalizationModal.vue';
import ProjectMoveResourceModal from '@/features/collaboration/projects/components/ProjectMoveResourceModal.vue';
import EventDestinationSettingsModal from '@/features/integrations/logStreaming.ee/components/EventDestinationSettingsModal.vue';
import SetupWorkflowCredentialsModal from '@/features/workflows/templates/components/SetupWorkflowCredentialsModal.vue';
import SourceControlPullModal from '@/features/integrations/sourceControl.ee/components/SourceControlPullModal.vue';
import SourceControlPullResultModal from '@/features/integrations/sourceControl.ee/components/SourceControlPullResultModal.vue';
import SourceControlPushModal from '@/features/integrations/sourceControl.ee/components/SourceControlPushModal.vue';
import AnnotationTagsManager from '@/features/shared/tags/components/TagsManager/AnnotationTagsManager.ee.vue';
import WorkflowTagsManager from '@/features/shared/tags/components/TagsManager/WorkflowTagsManager.vue';
import WhatsNewModal from '@/app/components/WhatsNewModal.vue';
import WorkflowActivationConflictingWebhookModal from '@/app/components/WorkflowActivationConflictingWebhookModal.vue';
import WorkflowExtractionNameModal from '@/app/components/WorkflowExtractionNameModal.vue';
import WorkflowHistoryVersionRestoreModal from '@/features/workflows/workflowHistory/components/WorkflowHistoryVersionRestoreModal.vue';
import WorkflowHistoryVersionUnpublishModal from '@/features/workflows/workflowHistory/components/WorkflowHistoryVersionUnpublishModal.vue';
import AgentConfirmationModal from '@/features/agents/components/AgentConfirmationModal.vue';
import type { AgentConfirmationModalData } from '@/features/agents/components/AgentConfirmationModal.vue';
import WorkflowVersionFormModal, {
	type WorkflowVersionFormModalData,
} from '@/features/workflows/workflowHistory/components/WorkflowVersionFormModal.vue';
import WorkflowSettings from '@/app/components/WorkflowSettings/WorkflowSettings.vue';
import WorkflowShareModal from '@/app/components/WorkflowShareModal.ee.vue';
import WorkflowDiffModal from '@/features/workflows/workflowDiff/WorkflowDiffModal.vue';
import type { EventBus } from '@n8n/utils/event-bus';
import PromptMfaCodeModal from '@/features/core/auth/components/PromptMfaCodeModal.vue';
import DynamicModalLoader from './DynamicModalLoader.vue';
import NodeRecommendationModalV2 from '@/experiments/templateRecoV2/components/NodeRecommendationModal.vue';
import NodeRecommendationModalV3 from '@/experiments/personalizedTemplatesV3/components/NodeRecommendationModal.vue';
import VariableModal from '@/features/settings/environments.ee/components/VariableModal.vue';
import StopManyExecutionsModal from './StopManyExecutionsModal.vue';
import AddExecutionToDatasetModal from '@/features/ai/evaluation.ee/components/AddExecutionToDataset/AddExecutionToDatasetModal.vue';
import WorkflowDescriptionModal from '@/app/components/WorkflowDescriptionModal.vue';
import WorkflowPublishModal from '@/app/components/MainHeader/WorkflowPublishModal.vue';
import MigrateWorkflowModal from '@/features/settings/migrationReport/MigrateWorkflowModal.vue';
import UpdatesPanel from './UpdatesPanel.vue';
import CredentialResolverEditModal from '@/app/components/CredentialResolverEditModal.vue';
import AIBuilderDiffModal from '@/features/ai/assistant/components/Agent/AIBuilderDiffModal.vue';
import AiGatewayTopUpModal from '@/features/ai/gateway/components/AiGatewayTopUpModal.vue';
import InstanceAiCredentialSetupModal, {
	type InstanceAiCredentialSetupModalData,
} from '@/features/ai/instanceAi/components/InstanceAiCredentialSetupModal.vue';
import InstanceAiToolsConnectionModalWrapper from '@/features/ai/instanceAi/components/modals/InstanceAiToolsConnectionModalWrapper.vue';
import { defineAsyncComponent } from 'vue';

const TrialIntroModal = defineAsyncComponent(
	async () => await import('@/experiments/trialIntroModal/components/TrialIntroModal.vue'),
);
</script>

<template>
	<div>
		<ModalRoot :name="CREDENTIAL_EDIT_MODAL_KEY">
			<template #default="{ modalName, activeId, mode }">
				<CredentialEdit :modal-name="modalName" :mode="mode" :active-id="activeId" />
			</template>
		</ModalRoot>

		<ModalRoot :name="API_KEY_CREATE_OR_EDIT_MODAL_KEY">
			<template
				#default="{
					modalName,
					data: { mode, activeId, rotatedApiKey },
				}: {
					modalName: string;
					data: {
						mode: 'new' | 'edit';
						activeId: string;
						rotatedApiKey?: ApiKeyWithRawValue | null;
					};
				}"
			>
				<ApiKeyCreateOrEditModal
					:modal-name="modalName"
					:mode="mode"
					:active-id="activeId"
					:rotated-api-key="rotatedApiKey"
				/>
			</template>
		</ModalRoot>

		<ModalRoot :name="ABOUT_MODAL_KEY">
			<AboutModal />
		</ModalRoot>

		<ModalRoot :name="CHAT_EMBED_MODAL_KEY">
			<ChatEmbedModal />
		</ModalRoot>

		<ModalRoot :name="CREDENTIAL_SELECT_MODAL_KEY">
			<CredentialsSelectModal />
		</ModalRoot>

		<ModalRoot :name="DUPLICATE_MODAL_KEY">
			<template #default="{ modalName, active, data }">
				<DuplicateWorkflowDialog :data="data" :is-active="active" :modal-name="modalName" />
			</template>
		</ModalRoot>

		<ModalRoot :name="IMPORT_WORKFLOW_URL_MODAL_KEY">
			<ImportWorkflowUrlModal />
		</ModalRoot>

		<ModalRoot :name="PERSONALIZATION_MODAL_KEY">
			<PersonalizationModal />
		</ModalRoot>

		<ModalRoot :name="TAGS_MANAGER_MODAL_KEY">
			<WorkflowTagsManager />
		</ModalRoot>

		<ModalRoot :name="ANNOTATION_TAGS_MANAGER_MODAL_KEY">
			<AnnotationTagsManager />
		</ModalRoot>

		<ModalRoot :name="VERSIONS_MODAL_KEY" :keep-alive="true">
			<UpdatesPanel />
		</ModalRoot>

		<ModalRoot :name="NPS_SURVEY_MODAL_KEY" :keep-alive="true">
			<template #default="{ active }">
				<NpsSurvey :is-active="active" />
			</template>
		</ModalRoot>

		<ModalRoot :name="WORKFLOW_SETTINGS_MODAL_KEY">
			<WorkflowSettings />
		</ModalRoot>

		<ModalRoot :name="CHANGE_PASSWORD_MODAL_KEY">
			<ChangePasswordModal />
		</ModalRoot>

		<ModalRoot :name="CONFIRM_PASSWORD_MODAL_KEY">
			<ConfirmPasswordModal />
		</ModalRoot>

		<ModalRoot :name="INVITE_USER_MODAL_KEY">
			<template #default="{ modalName, data }">
				<InviteUsersModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="DELETE_USER_MODAL_KEY">
			<template #default="{ modalName, data }">
				<DeleteUserModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="WORKFLOW_ACTIVE_MODAL_KEY">
			<ActivationModal />
		</ModalRoot>

		<ModalRoot :name="MFA_SETUP_MODAL_KEY">
			<MfaSetupModal />
		</ModalRoot>

		<ModalRoot :name="PROMPT_MFA_CODE_MODAL_KEY">
			<PromptMfaCodeModal />
		</ModalRoot>

		<ModalRoot :name="WORKFLOW_SHARE_MODAL_KEY">
			<template #default="{ modalName, active, data }">
				<WorkflowShareModal :data="data" :is-active="active" :modal-name="modalName" />
			</template>
		</ModalRoot>

		<ModalRoot :name="COMMUNITY_PACKAGE_INSTALL_MODAL_KEY">
			<CommunityPackageInstallModal />
		</ModalRoot>

		<ModalRoot :name="IMPORT_CURL_MODAL_KEY">
			<ImportCurlModal />
		</ModalRoot>

		<ModalRoot :name="COMMUNITY_PACKAGE_CONFIRM_MODAL_KEY">
			<template #default="{ modalName, activeId, mode }">
				<CommunityPackageManageConfirmModal
					:modal-name="modalName"
					:active-package-name="activeId"
					:mode="mode"
				/>
			</template>
		</ModalRoot>

		<ModalRoot :name="LOG_STREAM_MODAL_KEY">
			<template
				#default="{
					modalName,
					data,
				}: {
					modalName: string;
					data: { destination: Object; isNew: boolean; eventBus: EventBus };
				}"
			>
				<EventDestinationSettingsModal
					:modal-name="modalName"
					:destination="data.destination"
					:is-new="data.isNew"
					:event-bus="data.eventBus"
				/>
			</template>
		</ModalRoot>

		<ModalRoot :name="SOURCE_CONTROL_PUSH_MODAL_KEY">
			<template #default="{ modalName, data }">
				<SourceControlPushModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="SOURCE_CONTROL_PULL_MODAL_KEY">
			<template #default="{ modalName, data }">
				<SourceControlPullModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="SOURCE_CONTROL_PULL_RESULT_MODAL_KEY">
			<template #default="{ modalName, data }">
				<SourceControlPullResultModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="WORKFLOW_DIFF_MODAL_KEY">
			<template #default="{ modalName, data }">
				<WorkflowDiffModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="AI_BUILDER_DIFF_MODAL_KEY">
			<template #default="{ modalName, data }">
				<AIBuilderDiffModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="EXTERNAL_SECRETS_PROVIDER_MODAL_KEY">
			<template #default="{ modalName, data }">
				<ExternalSecretsProviderModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="SECRETS_PROVIDER_CONNECTION_MODAL_KEY">
			<template #default="{ modalName, data }">
				<SecretsProviderConnectionModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="DELETE_SECRETS_PROVIDER_MODAL_KEY">
			<template #default="{ modalName, data }">
				<DeleteSecretsProviderModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="DEBUG_PAYWALL_MODAL_KEY">
			<template #default="{ modalName, data }">
				<DebugPaywallModal
					data-test-id="debug-paywall-modal"
					:modal-name="modalName"
					:data="data"
				/>
			</template>
		</ModalRoot>

		<ModalRoot :name="WORKFLOW_HISTORY_VERSION_RESTORE">
			<template #default="{ modalName, data }">
				<WorkflowHistoryVersionRestoreModal
					data-test-id="workflow-history-version-restore-modal"
					:modal-name="modalName"
					:data="data"
				/>
			</template>
		</ModalRoot>

		<ModalRoot :name="WORKFLOW_HISTORY_VERSION_UNPUBLISH">
			<template #default="{ modalName, data }">
				<WorkflowHistoryVersionUnpublishModal
					data-test-id="workflow-history-version-unpublish-modal"
					:modal-name="modalName"
					:data="data"
				/>
			</template>
		</ModalRoot>

		<ModalRoot :name="AGENT_CONFIRMATION_MODAL_KEY">
			<template #default="{ modalName, data }">
				<AgentConfirmationModal
					:modal-name="modalName"
					:data="data as AgentConfirmationModalData"
				/>
			</template>
		</ModalRoot>

		<ModalRoot :name="WORKFLOW_HISTORY_NAME_VERSION_MODAL_KEY">
			<template #default="{ modalName, data }">
				<WorkflowVersionFormModal
					data-test-id="workflow-history-name-version-modal"
					:modal-name="modalName"
					:data="data as WorkflowVersionFormModalData"
				/>
			</template>
		</ModalRoot>

		<ModalRoot :name="SETUP_CREDENTIALS_MODAL_KEY">
			<template #default="{ modalName, data }">
				<SetupWorkflowCredentialsModal
					data-test-id="setup-workflow-credentials-modal"
					:modal-name="modalName"
					:data="data"
				/>
			</template>
		</ModalRoot>
		<ModalRoot :name="PROJECT_MOVE_RESOURCE_MODAL">
			<template #default="{ modalName, data }">
				<ProjectMoveResourceModal
					data-test-id="project-move-resource-modal"
					:modal-name="modalName"
					:data="data"
				/>
			</template>
		</ModalRoot>
		<ModalRoot :name="NEW_ASSISTANT_SESSION_MODAL">
			<template #default="{ modalName, data }">
				<NewAssistantSessionModal :name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="COMMUNITY_PLUS_ENROLLMENT_MODAL">
			<template #default="{ modalName, data }">
				<CommunityPlusEnrollmentModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="DELETE_FOLDER_MODAL_KEY">
			<template #default="{ modalName, activeId, data }">
				<DeleteFolderModal :modal-name="modalName" :active-id="activeId" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="MOVE_FOLDER_MODAL_KEY">
			<template #default="{ modalName, activeId, data }">
				<MoveToFolderModal :modal-name="modalName" :active-id="activeId" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="WORKFLOW_ACTIVATION_CONFLICTING_WEBHOOK_MODAL_KEY">
			<template #default="{ modalName, data }">
				<WorkflowActivationConflictingWebhookModal :data="data" :modal-name="modalName" />
			</template>
		</ModalRoot>

		<ModalRoot :name="FROM_AI_PARAMETERS_MODAL_KEY">
			<template #default="{ modalName, data }">
				<FromAiParametersModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="STOP_MANY_EXECUTIONS_MODAL_KEY">
			<template #default="{ modalName, data }">
				<StopManyExecutionsModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="ADD_EXECUTION_TO_DATASET_MODAL_KEY">
			<template #default="{ modalName, data }">
				<AddExecutionToDatasetModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="WORKFLOW_EXTRACTION_NAME_MODAL_KEY">
			<template #default="{ modalName, data }">
				<WorkflowExtractionNameModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="WHATS_NEW_MODAL_KEY">
			<template #default="{ modalName, data }">
				<WhatsNewModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="EXPERIMENT_TEMPLATE_RECO_V2_KEY">
			<template #default="{ modalName, data }">
				<NodeRecommendationModalV2 :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="EXPERIMENT_TEMPLATE_RECO_V3_KEY">
			<template #default="{ modalName, data }">
				<NodeRecommendationModalV3 :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="VARIABLE_MODAL_KEY">
			<template
				#default="{ data }: { data: { mode: 'new' | 'edit'; variable?: any; projectId?: string } }"
			>
				<VariableModal
					:mode="data?.mode ?? 'new'"
					:variable="data?.variable"
					:project-id="data?.projectId"
				/>
			</template>
		</ModalRoot>

		<ModalRoot :name="BINARY_DATA_VIEW_MODAL_KEY">
			<template #default="{ modalName, data }">
				<BinaryDataViewModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="WORKFLOW_DESCRIPTION_MODAL_KEY">
			<template #default="{ modalName, data }">
				<WorkflowDescriptionModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="WORKFLOW_PUBLISH_MODAL_KEY">
			<template #default="{ modalName, data }">
				<WorkflowPublishModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="MIGRATE_WORKFLOW_MODAL_KEY">
			<template #default="{ modalName, data }">
				<MigrateWorkflowModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="WORKFLOW_HISTORY_PUBLISH_MODAL_KEY">
			<template #default="{ modalName, data }">
				<WorkflowVersionFormModal
					:modal-name="modalName"
					:data="data as WorkflowVersionFormModalData"
				/>
			</template>
		</ModalRoot>

		<ModalRoot :name="CREDENTIAL_RESOLVER_EDIT_MODAL_KEY">
			<template #default="{ modalName, data }">
				<CredentialResolverEditModal :modal-name="modalName" :data="data" />
			</template>
		</ModalRoot>

		<ModalRoot :name="INSTANCE_AI_CREDENTIAL_SETUP_MODAL_KEY">
			<template #default="{ modalName, data }">
				<InstanceAiCredentialSetupModal
					data-test-id="instance-ai-credential-setup-modal"
					:modal-name="modalName"
					:data="data as InstanceAiCredentialSetupModalData"
				/>
			</template>
		</ModalRoot>

		<ModalRoot :name="INSTANCE_AI_TOOLS_CONNECTION_MODAL_KEY">
			<template #default="{ modalName }">
				<InstanceAiToolsConnectionModalWrapper :modal-name="modalName" />
			</template>
		</ModalRoot>

		<ModalRoot :name="AI_GATEWAY_TOP_UP_MODAL_KEY">
			<AiGatewayTopUpModal />
		</ModalRoot>

		<ModalRoot :name="TRIAL_INTRO_MODAL_KEY">
			<template #default="{ modalName }">
				<TrialIntroModal :modal-name="modalName" />
			</template>
		</ModalRoot>

		<!-- Dynamic modals from modules -->
		<DynamicModalLoader />
	</div>
</template>
